import { FileCategory, DocumentStatus } from "@/types/document.dto";
import type { Document } from "@/types/document.dto";

// Enum value to label maps
export const FileCategoryLabel: Record<FileCategory, string> = {
  [FileCategory.Other]: "Other",
  [FileCategory.Prescription]: "Prescriptions",
  [FileCategory.LabReport]: "Reports",
  [FileCategory.InsuranceCard]: "Insurance",
  [FileCategory.MedicalBill]: "Bills",
  [FileCategory.DischargeSummary]: "Procedures",
  [FileCategory.Identity]: "Identity",
  [FileCategory.EmergencyCard]: "Emergency Card",
};

// Generate options for selects using FileCategoryLabel
export const categoryOptions = [
  { label: "All", value: "" },
  ...Object.entries(FileCategoryLabel).map(([key, label]) => ({
    label,
    value: Number(key),
  })),
];

export const DocumentStatusLabel: Record<DocumentStatus, string> = {
  [DocumentStatus.Pending]: "Pending",
  [DocumentStatus.Processing]: "Processing",
  [DocumentStatus.Processed]: "Processed",
  [DocumentStatus.Failed]: "Failed",
  [DocumentStatus.Uncategorized]: "Uncategorized",
};

import { formatPrettyDate } from "@/utils/date-utils";

export const documentTableConfig = {
  defaultSort: { key: "createdAt", direction: "desc" },
  columns: [
    { key: "uid", label: "UID" },
    {
      key: "category",
      label: "Category",
      render: (value) => FileCategoryLabel[value as FileCategory] ?? value,
    },
    {
      key: "media.file_name",
      label: "File Name",
      render: (_: any, row: any) => {
        const media = row.media || {};
        return media.file_name || "";
      },
    },
    {
      key: "file_type",
      label: "File Type",
      render: (_: any, row: any) => {
        const media = row.media || {};
        const fileName = media.file_name || "";
        // First, try to extract the file extension from the file name (e.g., 'report.pdf' -> 'pdf').
        if (fileName && fileName.includes(".")) {
          return fileName.split(".").pop();
        }
        // If the file name does not have an extension, fall back to the MIME type (e.g., 'application/pdf' -> 'pdf').
        // This ensures we still display a file type even if the file name is missing or malformed.
        if (media.mime_type) {
          return media.mime_type.split("/").pop();
        }
        // If neither is available, return an empty string.
        return "";
      },
    },
    {
      key: "createdAt",
      label: "Uploaded At",
      sortable: true,
      sortType: "date",
      render: (value: any, row: any) => {
        // Try both snake_case and camelCase
        const dateValue = value || row.createdAt || row.created_at;
        return dateValue ? formatPrettyDate(dateValue) : "";
      },
    },
    {
      key: "status",
      label: "Status",
      render: (value) => DocumentStatusLabel[value as DocumentStatus] ?? value,
    },
  ] as any[],
  filters: [
    {
      key: "category",
      label: "Category",
      type: "select",
      options: categoryOptions,
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "All", value: "" },
        { label: "Pending", value: DocumentStatus.Pending },
        { label: "Processed", value: DocumentStatus.Processed },
        { label: "Failed", value: DocumentStatus.Failed },
      ],
    },
    {
      key: "media.file_name",
      label: "Search the Document",
      type: "text",
    },
  ],
};
